import { CSSProperties, FC, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui'
import { ResponseThread } from '@/schema'
import { cn } from '@/utils'

import { EStickyPosition } from '../../types'

import CommentItem from './CommentItem'

const STICKY_THRESHOLD = 2
const FIRST_STICKY_THRESHOLD = 5

interface CommentGroupProps {
  title: string
  comments: ResponseThread[]
  defaultOpen?: boolean
  isFirst?: boolean
  layoutVersion?: number
  onLayoutChange?: () => void
}

export const CommentGroup: FC<CommentGroupProps> = ({
  title,
  comments,
  defaultOpen = true,
  isFirst = false,
  layoutVersion = 0,
  onLayoutChange,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [stickyPosition, setStickyPosition] = useState<EStickyPosition | null>(
    null
  )
  const [fixedStyle, setFixedStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  const lastStickyPositionRef = useRef<EStickyPosition | null>(null)

  // Sync ref with stickyPosition state to track previous position across effect runs
  useEffect(() => {
    lastStickyPositionRef.current = stickyPosition
  }, [stickyPosition])

  // Notify parent when open state changes to trigger layout recalculation in other groups
  // Use useEffect to watch isOpen changes and notify after DOM updates
  useEffect(() => {
    // Skip notification on initial mount
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (onLayoutChange) {
      // Use requestAnimationFrame to ensure DOM has updated after collapse/expand animation
      const rafId = requestAnimationFrame(() => {
        // Add a small delay to ensure the CollapsibleContent animation has started
        setTimeout(() => {
          onLayoutChange()
        }, 50)
      })
      return () => cancelAnimationFrame(rafId)
    }
  }, [isOpen, onLayoutChange, title])

  // Check trigger position relative to container using wrapper div (stable reference)
  // Only run when isOpen is true (expanded) - skip when collapsed
  useEffect(() => {
    // If collapsed, reset sticky position and don't set up event listeners
    if (!isOpen) {
      lastStickyPositionRef.current = null
      setStickyPosition(null)
      setFixedStyle({})
      return
    }

    const trigger = triggerRef.current
    const container = document.getElementById('comment-list-container')
    const wrapper = containerRef.current

    if (!trigger || !container || !wrapper) return

    let rafId: number | null = null

    const checkPosition = () => {
      if (!trigger || !container || !wrapper) return

      // Use wrapper div's position (doesn't change when trigger becomes sticky)
      const wrapperRect = wrapper.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const triggerHeight = trigger.offsetHeight
      const containerTop = containerRect.top
      const containerBottom = containerRect.bottom

      // Calculate trigger's position based on wrapper
      // The trigger sits at the top of the wrapper
      const triggerTop = wrapperRect.top
      const triggerBottom = wrapperRect.top + triggerHeight

      // Add a threshold to prevent flickering during slow scrolling
      // Use a larger threshold for the first element to make it smoother
      const threshold = isFirst ? FIRST_STICKY_THRESHOLD : STICKY_THRESHOLD

      // Check if trigger's bottom is above container's top (scrolled past upward)
      const isAboveViewport = triggerBottom < containerTop - threshold

      // Check if trigger's top is below container's bottom (not yet in view)
      const isBelowViewport = triggerTop > containerBottom + threshold

      let newStickyPosition: EStickyPosition | null = null
      let newFixedStyle: CSSProperties = {}

      if (isAboveViewport) {
        // Scrolled past upward - stick to top
        newStickyPosition = EStickyPosition.TOP
      } else if (isBelowViewport) {
        // Not yet scrolled to - stick to bottom
        // Calculate fixed position relative to container
        newStickyPosition = EStickyPosition.BOTTOM
        newFixedStyle = {
          left: containerRect.left,
          width: containerRect.width,
          bottom: window.innerHeight - containerRect.bottom,
        }
      }
      // If isInViewport, newStickyPosition remains null (not sticky)

      // Only update state if position changed to prevent unnecessary re-renders
      // Compare against the ref value which persists across effect runs
      const currentStickyPosition = lastStickyPositionRef.current
      if (newStickyPosition === currentStickyPosition) {
        return
      }

      lastStickyPositionRef.current = newStickyPosition
      setStickyPosition(newStickyPosition)
      setFixedStyle(newFixedStyle)
    }

    const handleScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(checkPosition)
    }

    // Initial check with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      checkPosition()
    }, 0)

    // Listen to scroll events on the container with requestAnimationFrame
    container.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })

    return () => {
      clearTimeout(timeoutId)
      if (rafId !== null) cancelAnimationFrame(rafId)

      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [comments.length, isFirst, isOpen, layoutVersion, title])

  if (comments.length === 0) return null
  return (
    <div ref={containerRef} className="relative">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger
          asChild
          className="w-full"
          aria-label={isOpen ? 'Collapse section' : 'Expand section'}
        >
          <button
            ref={triggerRef}
            type="button"
            className={cn(
              'flex items-center justify-between border-none bg-transparent cursor-pointer',
              'pt-3 px-4',
              'border border-solid border-secondary border-b-0 border-x-0',
              'data-[state=closed]:pb-3',
              isFirst ? 'border-t-0' : '',
              stickyPosition && 'sticky z-10 bg-white pb-3',
              stickyPosition === EStickyPosition.TOP && 'top-0',
              stickyPosition === EStickyPosition.BOTTOM && 'bottom-0'
            )}
            style={{
              position: (() => {
                if (stickyPosition === EStickyPosition.BOTTOM) return 'fixed'
                if (stickyPosition === EStickyPosition.TOP) return 'sticky'
                return 'relative'
              })(),
              top: stickyPosition === EStickyPosition.TOP ? 0 : 'auto',
              bottom:
                stickyPosition === EStickyPosition.BOTTOM
                  ? (fixedStyle.bottom ?? 0)
                  : 'auto',
              left:
                stickyPosition === EStickyPosition.BOTTOM
                  ? fixedStyle.left
                  : 'auto',
              width:
                stickyPosition === EStickyPosition.BOTTOM
                  ? fixedStyle.width
                  : '100%',
              zIndex: stickyPosition ? 10 : 'auto',
              borderBottom:
                stickyPosition === EStickyPosition.TOP
                  ? '1px solid var(--Colors-Border-border-secondary)'
                  : '',
              borderTop: stickyPosition === EStickyPosition.TOP ? 'none' : '',
            }}
          >
            <span className="uppercase text-xs font-medium text-primary-900">
              {title} ({comments.length})
            </span>
            <ChevronDown
              className={cn(
                'ml-auto transition-transform duration-200 text-foreground-fg-quaternary-400',
                isOpen ? 'rotate-180' : ''
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(
            'overflow-hidden transition-all duration-300 ease-in-out'
          )}
        >
          <div className="min-h-0 py-1 px-2 space-y-1">
            {comments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
